/**
 * JourneyFormBuilder Component
 *
 * Visual form builder for journey configuration.
 * Supports all step types: form, results, details, click_out.
 */

import type { JourneyConfig, JourneyStep } from "@tatvex/config";
import {
  Route,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  FormInput,
  LayoutList,
  FileText,
  ExternalLink,
  ArrowRight,
  Play,
} from "lucide-react";
import { useCallback, useState } from "react";

import type { JourneyFormBuilderProps, JourneyStepType, FieldError } from "./types";
import { createEmptyJourneyStep, generateId } from "./types";

// ============================================================================
// Step Type Icons
// ============================================================================

const STEP_TYPE_CONFIG: Record<
  JourneyStepType,
  { icon: React.ElementType; label: string; color: string }
> = {
  form: { icon: FormInput, label: "Form", color: "blue" },
  results: { icon: LayoutList, label: "Results", color: "green" },
  details: { icon: FileText, label: "Details", color: "purple" },
  click_out: { icon: ExternalLink, label: "Click Out", color: "orange" },
};

// ============================================================================
// Form Step Config Sub-Component
// ============================================================================

interface FormStepConfigProps {
  readonly config: Record<string, unknown>;
  readonly onChange: (config: Record<string, unknown>) => void;
  readonly readOnly?: boolean;
}

function FormStepConfig({
  config,
  onChange,
  readOnly = false,
}: FormStepConfigProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            value={(config["title"] as string) ?? ""}
            onChange={(e) => onChange({ ...config, title: e.target.value })}
            readOnly={readOnly}
            placeholder="Step Title"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Submit Label</label>
          <input
            type="text"
            value={(config["submitLabel"] as string) ?? "Continue"}
            onChange={(e) => onChange({ ...config, submitLabel: e.target.value })}
            readOnly={readOnly}
            placeholder="Continue"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">Description</label>
        <input
          type="text"
          value={(config["description"] as string) ?? ""}
          onChange={(e) => onChange({ ...config, description: e.target.value || undefined })}
          readOnly={readOnly}
          placeholder="Optional description..."
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["allowSkip"] as boolean) ?? false}
            onChange={(e) => onChange({ ...config, allowSkip: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Allow Skip</span>
        </label>
        {Boolean(config["allowSkip"]) && (
          <div className="flex flex-col gap-1">
            <input
              type="text"
              value={(config["skipLabel"] as string) ?? "Skip"}
              onChange={(e) => onChange({ ...config, skipLabel: e.target.value || undefined })}
              readOnly={readOnly}
              placeholder="Skip Label"
              className="w-32 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </div>
        )}
      </div>

      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
        <p className="text-sm text-gray-600">
          <strong>Note:</strong> Form fields are configured separately in the schema. Use{" "}
          <code className="rounded bg-gray-200 px-1 text-xs">schemaRef</code> to reference a schema
          or define inline fields.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Results Step Config Sub-Component
// ============================================================================

interface ResultsStepConfigProps {
  readonly config: Record<string, unknown>;
  readonly onChange: (config: Record<string, unknown>) => void;
  readonly readOnly?: boolean;
}

function ResultsStepConfig({
  config,
  onChange,
  readOnly = false,
}: ResultsStepConfigProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            value={(config["title"] as string) ?? "Results"}
            onChange={(e) => onChange({ ...config, title: e.target.value })}
            readOnly={readOnly}
            placeholder="Results"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Default Strategy</label>
          <input
            type="text"
            value={(config["defaultStrategy"] as string) ?? ""}
            onChange={(e) => onChange({ ...config, defaultStrategy: e.target.value })}
            readOnly={readOnly}
            placeholder="best_value"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Results Per Page</label>
          <input
            type="number"
            value={(config["resultsPerPage"] as number) ?? 10}
            onChange={(e) =>
              onChange({ ...config, resultsPerPage: parseInt(e.target.value, 10) || 10 })
            }
            readOnly={readOnly}
            min={1}
            max={100}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-end gap-4 pb-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={(config["showStrategySelector"] as boolean) ?? true}
              onChange={(e) => onChange({ ...config, showStrategySelector: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Show Strategy Selector</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={(config["showRankExplanation"] as boolean) ?? true}
              onChange={(e) => onChange({ ...config, showRankExplanation: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Show Rank Explanation</span>
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">Empty State Message</label>
        <input
          type="text"
          value={(config["emptyStateMessage"] as string) ?? ""}
          onChange={(e) => onChange({ ...config, emptyStateMessage: e.target.value || undefined })}
          readOnly={readOnly}
          placeholder="No results match your criteria..."
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
}

// ============================================================================
// Details Step Config Sub-Component
// ============================================================================

interface DetailsStepConfigProps {
  readonly config: Record<string, unknown>;
  readonly onChange: (config: Record<string, unknown>) => void;
  readonly readOnly?: boolean;
}

function DetailsStepConfig({
  config,
  onChange,
  readOnly = false,
}: DetailsStepConfigProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["showBackButton"] as boolean) ?? true}
            onChange={(e) => onChange({ ...config, showBackButton: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Show Back Button</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["showCompareButton"] as boolean) ?? false}
            onChange={(e) => onChange({ ...config, showCompareButton: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Show Compare Button</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["showCtaButton"] as boolean) ?? true}
            onChange={(e) => onChange({ ...config, showCtaButton: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Show CTA Button</span>
        </label>
      </div>

      {Boolean(config["showCtaButton"]) && (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">CTA Button Label</label>
          <input
            type="text"
            value={(config["ctaButtonLabel"] as string) ?? ""}
            onChange={(e) => onChange({ ...config, ctaButtonLabel: e.target.value || undefined })}
            readOnly={readOnly}
            placeholder="Get This Plan"
            className="w-48 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
        <p className="text-sm text-gray-600">
          <strong>Sections:</strong> Configure attribute sections in the JSON view for full control
          over detail page layout.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Click Out Step Config Sub-Component
// ============================================================================

interface ClickOutStepConfigProps {
  readonly config: Record<string, unknown>;
  readonly onChange: (config: Record<string, unknown>) => void;
  readonly readOnly?: boolean;
}

function ClickOutStepConfig({
  config,
  onChange,
  readOnly = false,
}: ClickOutStepConfigProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["trackingEnabled"] as boolean) ?? true}
            onChange={(e) => onChange({ ...config, trackingEnabled: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Enable Tracking</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["openInNewTab"] as boolean) ?? true}
            onChange={(e) => onChange({ ...config, openInNewTab: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Open in New Tab</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={(config["showInterstitial"] as boolean) ?? false}
            onChange={(e) => onChange({ ...config, showInterstitial: e.target.checked })}
            disabled={readOnly}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Show Interstitial</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Redirect Delay (ms)</label>
          <input
            type="number"
            value={(config["redirectDelay"] as number) ?? 1500}
            onChange={(e) =>
              onChange({
                ...config,
                redirectDelay: parseInt(e.target.value, 10) || undefined,
              })
            }
            readOnly={readOnly}
            min={0}
            max={10000}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Confirmation Message</label>
          <input
            type="text"
            value={(config["confirmationMessage"] as string) ?? ""}
            onChange={(e) =>
              onChange({ ...config, confirmationMessage: e.target.value || undefined })
            }
            readOnly={readOnly}
            placeholder="Redirecting you..."
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>

      {Boolean(config["showInterstitial"]) && (
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Interstitial Message</label>
          <textarea
            value={(config["interstitialMessage"] as string) ?? ""}
            onChange={(e) =>
              onChange({ ...config, interstitialMessage: e.target.value || undefined })
            }
            readOnly={readOnly}
            placeholder="You are being redirected to the provider's website..."
            rows={2}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Step Card Sub-Component
// ============================================================================

interface StepCardProps {
  readonly value: JourneyStep;
  readonly onChange: (value: JourneyStep) => void;
  readonly onDelete: () => void;
  readonly availableStepIds: readonly string[];
  readonly isEntryStep: boolean;
  readonly readOnly?: boolean;
}

function StepCard({
  value,
  onChange,
  onDelete,
  availableStepIds,
  isEntryStep,
  readOnly = false,
}: StepCardProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);

  const typeConfig = STEP_TYPE_CONFIG[value.type as JourneyStepType];
  const Icon = typeConfig.icon;

  const handleFieldChange = useCallback(
    <K extends keyof JourneyStep>(key: K, newValue: JourneyStep[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  const handleTypeChange = useCallback(
    (newType: JourneyStepType) => {
      const newStep = createEmptyJourneyStep(newType);
      onChange({
        ...value,
        type: newType,
        config: newStep.config,
      } as JourneyStep);
    },
    [value, onChange]
  );

  const handleNextStepChange = useCallback(
    (index: number, stepId: string) => {
      const nextSteps = [...(value.nextSteps ?? [])];
      nextSteps[index] = { ...nextSteps[index], stepId };
      handleFieldChange("nextSteps", nextSteps);
    },
    [value.nextSteps, handleFieldChange]
  );

  const handleAddNextStep = useCallback(() => {
    const nextSteps = [...(value.nextSteps ?? []), { stepId: "" }];
    handleFieldChange("nextSteps", nextSteps);
  }, [value.nextSteps, handleFieldChange]);

  const handleDeleteNextStep = useCallback(
    (index: number) => {
      const nextSteps = (value.nextSteps ?? []).filter((_, i) => i !== index);
      handleFieldChange("nextSteps", nextSteps.length > 0 ? nextSteps : undefined);
    },
    [value.nextSteps, handleFieldChange]
  );

  const colorClasses: Record<string, string> = {
    blue: "border-blue-200 bg-blue-50",
    green: "border-green-200 bg-green-50",
    purple: "border-purple-200 bg-purple-50",
    orange: "border-orange-200 bg-orange-50",
  };

  const iconColorClasses: Record<string, string> = {
    blue: "text-blue-500",
    green: "text-green-500",
    purple: "text-purple-500",
    orange: "text-orange-500",
  };

  return (
    <div className="step-card rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between border-b border-gray-100 px-4 py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`rounded-lg border p-2 ${colorClasses[typeConfig.color]}`}>
            <Icon className={`h-5 w-5 ${iconColorClasses[typeConfig.color]}`} />
          </div>
          <div>
            <h4 className="flex items-center gap-2 font-medium text-gray-900">
              {value.id || "Unnamed Step"}
              {isEntryStep && (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                  <Play className="mr-1 h-3 w-3" />
                  Entry
                </span>
              )}
            </h4>
            <p className="text-sm text-gray-500">
              {typeConfig.label} Step
              {value.nextSteps && value.nextSteps.length > 0 && (
                <span className="ml-2">
                  → {value.nextSteps.map((n) => n.stepId || "?").join(", ")}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!readOnly && !isEntryStep && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="space-y-4 p-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Step ID</label>
              <input
                type="text"
                value={value.id}
                onChange={(e) => handleFieldChange("id", e.target.value)}
                readOnly={readOnly}
                placeholder="step_id"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Step Type</label>
              <select
                value={value.type}
                onChange={(e) => handleTypeChange(e.target.value as JourneyStepType)}
                disabled={readOnly}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {Object.entries(STEP_TYPE_CONFIG).map(([type, cfg]) => (
                  <option key={type} value={type}>
                    {cfg.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Type-specific config */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
            <h5 className="mb-3 text-sm font-medium text-gray-700">
              {typeConfig.label} Configuration
            </h5>
            {value.type === "form" && (
              <FormStepConfig
                config={value.config as Record<string, unknown>}
                onChange={(c) => handleFieldChange("config", c as JourneyStep["config"])}
                readOnly={readOnly}
              />
            )}
            {value.type === "results" && (
              <ResultsStepConfig
                config={value.config as Record<string, unknown>}
                onChange={(c) => handleFieldChange("config", c as JourneyStep["config"])}
                readOnly={readOnly}
              />
            )}
            {value.type === "details" && (
              <DetailsStepConfig
                config={value.config as Record<string, unknown>}
                onChange={(c) => handleFieldChange("config", c as JourneyStep["config"])}
                readOnly={readOnly}
              />
            )}
            {value.type === "click_out" && (
              <ClickOutStepConfig
                config={value.config as Record<string, unknown>}
                onChange={(c) => handleFieldChange("config", c as JourneyStep["config"])}
                readOnly={readOnly}
              />
            )}
          </div>

          {/* Next Steps */}
          {value.type !== "click_out" && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Next Steps</label>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={handleAddNextStep}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="h-4 w-4" />
                    Add Transition
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {(value.nextSteps ?? []).map((next, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2"
                  >
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <select
                      value={next.stepId}
                      onChange={(e) => handleNextStepChange(idx, e.target.value)}
                      disabled={readOnly}
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                    >
                      <option value="">Select step...</option>
                      {availableStepIds
                        .filter((id) => id !== value.id)
                        .map((id) => (
                          <option key={id} value={id}>
                            {id}
                          </option>
                        ))}
                    </select>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleDeleteNextStep(idx)}
                        className="rounded p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}

                {(!value.nextSteps || value.nextSteps.length === 0) && (
                  <p className="py-2 text-center text-sm text-gray-500">
                    No transitions defined.{" "}
                    {(value.type as string) !== "click_out" &&
                      "Add a transition to connect to the next step."}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * JourneyFormBuilder - Visual journey configuration
 *
 * CSS Classes:
 * - journey-form: Container element
 * - journey-form__header: Header with title and add button
 * - journey-form__steps: Step cards container
 */
export function JourneyFormBuilder({
  value,
  onChange,
  onValidate,
  readOnly = false,
  errors = [],
  className = "",
}: JourneyFormBuilderProps): React.JSX.Element {
  const [localErrors, setLocalErrors] = useState<readonly FieldError[]>([]);
  // Merge errors from external and local validation
  void [...errors, ...localErrors];

  const availableStepIds = value.steps.map((s) => s.id);

  const handleChange = useCallback(
    <K extends keyof JourneyConfig>(key: K, newValue: JourneyConfig[K]) => {
      const updated: JourneyConfig = {
        ...value,
        [key]: newValue,
      };

      if (onValidate) {
        const result = onValidate(updated);
        setLocalErrors(result.errors);
      }

      onChange(updated);
    },
    [value, onChange, onValidate]
  );

  const handleStepChange = useCallback(
    (index: number, newStep: JourneyStep) => {
      const newSteps = [...value.steps];
      newSteps[index] = newStep;
      handleChange("steps", newSteps);
    },
    [value.steps, handleChange]
  );

  const handleAddStep = useCallback(
    (type: JourneyStepType) => {
      const newStep = {
        ...createEmptyJourneyStep(type),
        id: generateId(type),
      };
      handleChange("steps", [...value.steps, newStep as JourneyStep]);
    },
    [value.steps, handleChange]
  );

  const handleDeleteStep = useCallback(
    (index: number) => {
      const deletedId = value.steps[index]?.id;
      const newSteps = value.steps.filter((_, i) => i !== index);
      handleChange("steps", newSteps);

      // Update entry step if deleted
      if (deletedId === value.entryStep && newSteps.length > 0) {
        handleChange("entryStep", newSteps[0]?.id ?? "");
      }
    },
    [value.steps, value.entryStep, handleChange]
  );

  return (
    <div className={`journey-form flex flex-col gap-6 ${className}`}>
      {/* Header Info */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Vertical Key</label>
            <input
              type="text"
              value={value.verticalKey}
              onChange={(e) => handleChange("verticalKey", e.target.value)}
              readOnly={readOnly}
              placeholder="vertical-key"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Version</label>
            <input
              type="number"
              value={value.version}
              onChange={(e) => handleChange("version", parseInt(e.target.value, 10) || 1)}
              readOnly={readOnly}
              min={1}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Entry Step</label>
            <select
              value={value.entryStep}
              onChange={(e) => handleChange("entryStep", e.target.value)}
              disabled={readOnly}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Select entry step...</option>
              {value.steps
                .filter((s) => s.type === "form")
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <div>
        <div className="journey-form__header mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Journey Steps</h3>
          {!readOnly && (
            <div className="flex gap-2">
              {(
                Object.entries(STEP_TYPE_CONFIG) as [
                  JourneyStepType,
                  typeof STEP_TYPE_CONFIG.form,
                ][]
              ).map(([type, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleAddStep(type)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Icon className="h-4 w-4" />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="journey-form__steps space-y-4">
          {value.steps.map((step, index) => (
            <StepCard
              key={step.id || index}
              value={step}
              onChange={(s) => handleStepChange(index, s)}
              onDelete={() => handleDeleteStep(index)}
              availableStepIds={availableStepIds}
              isEntryStep={step.id === value.entryStep}
              readOnly={readOnly}
            />
          ))}
        </div>

        {value.steps.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-gray-300 py-8 text-center">
            <Route className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No journey steps defined.</p>
            {!readOnly && (
              <button
                type="button"
                onClick={() => handleAddStep("form")}
                className="mt-4 inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Add Form Step
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export type { JourneyFormBuilderProps };
